import {
  allStreaks,
  bestPerManager,
  seasonBoards,
  seasonTallies,
  singleGameBoards,
  streakBoards,
  tallyPct,
  type History,
  type RecordBoard,
  type RecordRow,
  type ValueKind,
} from './records';
import type { TradeRecord } from './tradetree';

/**
 * The all-time facts the home page leads with.
 *
 * Every row here is lifted from the boards `/records` already builds rather
 * than re-derived, so the landing page cannot quietly disagree with the record
 * book it links to. The only thing computed here is the tie count, which the
 * boards do not expose.
 */
export interface Highlight {
  key: string;
  title: string;
  kind: ValueKind;
  row: RecordRow;
  /**
   * How many entries hold the top value EXACTLY, including this one. A record
   * card that prints one name while two managers share the record is wrong in
   * the way nobody checks -- 2025 already has it, with two 11-5 seasons.
   */
  tied: number;
  /**
   * The OTHER holders of that value, so the card can name them rather than
   * print "+1 tied" and make the reader open another page.
   *
   * Taken from the board, which caps at five rows, while `tied` is counted off
   * the source data. So this can be shorter than `tied - 1`, and the card has
   * to fall back to the count when it is -- naming eleven co-holders would not
   * fit a card anyway.
   */
  tiedWith: string[];
}

/**
 * A board the home page wants that cannot be built from `/api/scores` alone.
 *
 * Efficiency is the instance: potential points reach the client on
 * `TeamRecord`, via `/api/history` and the current season's roster list, so
 * there is no game row to derive it from. Handed in rather than found here,
 * which keeps this module free of the league bundle.
 *
 * `all` is the board's UNCAPPED source rows, and passing it is the whole point
 * of the pair. `board()` slices to five, so a tie count taken off `board.rows`
 * would report five for a six-way tie -- the same reason every case below
 * counts off source data rather than off the board.
 */
export interface ExtraBoard {
  board: RecordBoard;
  all: RecordRow[];
}

/** Rows in a board that match its leader's value. Counted off the source data,
 *  not the board, because `board()` caps its rows at five. */
function tiesFor(key: string, flat: History, top: RecordRow, extra: ExtraBoard[]): number {
  // An extra board carries its own source rows, so it needs no case below.
  // Matched on the ranked value AND the rendered detail, for the same reason
  // `tiedWith` matches on both: a board may sort on one number and print
  // another, and matching the number alone would file two different records
  // under one headline.
  const ex = extra.find((e) => e.board.key === key);
  if (ex) return ex.all.filter((r) => r.value === top.value && r.detail === top.detail).length;

  switch (key) {
    case 'high':
      return flat.appearances.filter((a) => a.points === top.value).length;
    case 'season-points':
      return seasonTallies(flat.appearances).filter((t) => t.points === top.value).length;
    case 'blowout':
      return flat.appearances.filter((a) => a.result === 'W' && a.margin === top.value).length;
    case 'unlucky':
      return flat.appearances.filter((a) => a.result === 'L' && a.points === top.value).length;
    case 'win-streak':
      // One run per manager, matching what the board ranks. Counting raw
      // streaks would file a manager's second-best run as a second holder of
      // the record. This board is the reason the default below is dangerous:
      // its values are small integers, so a tie at the top is likely rather
      // than freakish -- 2025 already has two managers one behind the leader.
      return bestPerManager(allStreaks(flat.appearances).filter((s) => s.kind === 'W')).filter(
        (s) => s.length === top.value,
      ).length;
    case 'best-record':
      // The board drops short seasons before ranking; a tie count taken over
      // the unfiltered tallies would include a 2-game cameo at 1.000.
      return seasonTallies(flat.appearances).filter(
        (t) => t.games >= 4 && tallyPct(t) === top.value,
      ).length;
    default:
      // Any board reaching this reports "no tie" whatever the data says, which
      // silently drops the "and N more" line. A new key on the home page needs
      // a case above -- or, if it arrives as an `ExtraBoard`, nothing at all,
      // since those are counted from their own rows before the switch. See the
      // co-holder rule in CLAUDE.md.
      return 1;
  }
}

/**
 * Pick named boards down to their leader.
 *
 * Returns only the boards that have a leader, so a league with no games yet
 * yields an empty array rather than a row of blanks. A key naming an `extra`
 * board resolves the same way as a built-in one.
 */
export function topHighlights(
  flat: History,
  keys: string[],
  extra: ExtraBoard[] = [],
): Highlight[] {
  const boards: RecordBoard[] = [
    ...singleGameBoards(flat),
    ...seasonBoards(flat.appearances),
    ...streakBoards(flat.appearances),
    ...extra.map((e) => e.board),
  ];
  const out: Highlight[] = [];

  for (const key of keys) {
    const board = boards.find((b) => b.key === key);
    const row = board?.rows[0];
    if (!board || !row) continue;
    out.push({
      key,
      title: board.title,
      kind: board.kind,
      row,
      tied: tiesFor(key, flat, row, extra),
      // Boards are sorted, so anything sharing the leader's value is adjacent
      // to it. Both the ranked value AND the rendered detail have to match:
      // `best-record` ranks on win rate but PRINTS the record, so two managers
      // can tie on the number while showing different text, and listing them
      // under one headline would attribute a record nobody had.
      tiedWith: board.rows
        .slice(1)
        .filter((r) => r.value === row.value && r.detail === row.detail)
        .map((r) => r.userId),
    });
  }

  return out;
}

// --- Trades ----------------------------------------------------------------

export interface TraderRow {
  userId: string;
  /** Starter points received across completed trades. */
  received: number;
  /** What the other sides received from those same trades. */
  givenUp: number;
  /** `received - givenUp`. Negative means the other sides scored more. */
  net: number;
  /** Completed trades this manager was a side of. */
  trades: number;
}

/**
 * Net starter points each manager has drawn from their COMPLETED trades.
 *
 * Three deliberate limits, each inherited from the ledger rather than invented
 * here:
 *
 * 1. **Completed trades only.** A trade still holding an undrafted pick, or one
 *    drafted into a season nobody has played, is excluded outright -- the same
 *    rule that makes `TradeRecord.spread` null. Counting it would score a side
 *    zero for an asset that has not had its chance, and the bias always runs
 *    against the side holding the pick, so no caveat repairs it. The cost is
 *    that the ranking covers a SUBSET, and which trades fall out is not random:
 *    pick-heavy trades are exactly the ones excluded. `tradeExtremes` reports
 *    the count so the card can say what it is built from.
 *
 * 2. **Starter points only.** Bench production never reached anyone's score.
 *
 * 3. **Running totals.** A player acquired last August is still scoring. Nothing
 *    here is a final verdict and the UI has to say so.
 *
 * What a side "gave up" is the MEAN of what the other sides received, not the
 * sum: in a three-way trade the sum would count the same points against two
 * different managers and make every multi-way trade look like a fleecing. With
 * two sides -- every trade in this league so far -- the mean is just the other
 * side's total, so the choice only shows up the first time someone does a
 * three-way.
 */
export function traderStandings(ledger: TradeRecord[]): TraderRow[] {
  const rows = new Map<string, TraderRow>();

  for (const trade of ledger) {
    if (!trade.complete) continue;

    for (const side of trade.sides) {
      if (!side.userId) continue;
      const others = trade.sides.filter((o) => o !== side);
      if (others.length === 0) continue;

      const givenUp = others.reduce((sum, o) => sum + o.points, 0) / others.length;
      const row = rows.get(side.userId) ?? {
        userId: side.userId,
        received: 0,
        givenUp: 0,
        net: 0,
        trades: 0,
      };

      row.received += side.points;
      row.givenUp += givenUp;
      row.net = row.received - row.givenUp;
      row.trades += 1;
      rows.set(side.userId, row);
    }
  }

  // Round once at the end. Accumulating rounded hundredths drifts, and these
  // totals run to four figures.
  for (const row of rows.values()) {
    row.received = Math.round(row.received * 100) / 100;
    row.givenUp = Math.round(row.givenUp * 100) / 100;
    row.net = Math.round(row.net * 100) / 100;
  }

  return [...rows.values()].sort((a, b) => b.net - a.net || b.trades - a.trades);
}

export interface TradeExtremes {
  best: TraderRow | null;
  worst: TraderRow | null;
  /** Completed trades the standings are built from. */
  counted: number;
  /** Trades excluded because an asset has not had its chance yet. */
  pending: number;
}

/**
 * The two ends of `traderStandings`, plus the basis to disclose.
 *
 * `best` and `worst` are null until at least two managers have a completed
 * trade -- with one, the same manager is both ends of the table and the card
 * would print them twice, which is the "card printing 11-5 as both headline and
 * subtitle" defect the record book already shipped once.
 */
export function tradeExtremes(ledger: TradeRecord[]): TradeExtremes {
  const rows = traderStandings(ledger);
  const counted = ledger.filter((t) => t.complete).length;
  const pending = ledger.length - counted;

  if (rows.length < 2) return { best: null, worst: null, counted, pending };
  return { best: rows[0], worst: rows[rows.length - 1], counted, pending };
}
